import React from 'react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { ReadingMeta, SourcesBlock } from '../components/ReadingMeta';
import TableOfContents from '../components/TableOfContents';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import ScienceChart from '../components/ScienceChart';
import { Reveal } from '../components/Reveal';
import { CEMENT_SUB_CHART, CEMENT_SUB_METHOD, MIX_COMPARISON } from '../data/science';
import { SITE_URL, CONTACT } from '../data/site';
import AnswerBlock from '../components/geo/AnswerBlock';
import Breadcrumbs from '../components/geo/Breadcrumbs';
import AuthorMeta from '../components/geo/AuthorMeta';
import RelatedTopics from '../components/geo/RelatedTopics';
import FAQ from '../components/geo/FAQ';
import { FAQS, RELATED, PAGE_DATES } from '../data/geo';

const Zementsubstitution = () => {
  const { t, lang } = useLang();
  const c = CEMENT_SUB_CHART;
  const answer = lang === 'de'
    ? 'Metakaolin kann in abgestimmten Bindemittelsystemen einen Teil des Zementklinkers ersetzen und so den klinkerbezogenen CO\u2082-Fu\u00dfabdruck senken. Ob und in welchem Umfang dies ohne Leistungseinbu\u00dfe m\u00f6glich ist, h\u00e4ngt von Ausgangsstoffen, Dosierung, w/b-Wert und Anforderungen ab und ist projektspezifisch durch Eignungspr\u00fcfungen zu bewerten.'
    : 'In tailored binder systems, metakaolin can replace part of the cement clinker and thereby lower the clinker-related CO\u2082 footprint. Whether and to what extent this is possible without loss of performance depends on raw materials, dosage, w/b ratio and requirements and must be verified per project by suitability testing.';
  const chartSummary = lang === 'de'
    ? 'Interpretation: Im dargestellten Versuch (Metaver\u00ae K) lag das System mit Metakaolinersatz beim puzzolanischen Index oberhalb des Referenzzements. Das kombinierte System mit 26 M.-% Klinkerersatz (8 M.-% Metaver\u00ae K + 18 M.-% Kalksteinmehl) n\u00e4herte sich bis 28 Tage dem Niveau des Referenzzements an. \u00dcber die gesamte Versuchsreihe (> 30 Mischungen) konnte bis zu 26 M.-% Klinker ersetzt werden, ohne die Festigkeit wesentlich zu verringern. Der Referenzzement entspricht per Definition 100 %. Die Werte gelten f\u00fcr die gepr\u00fcften Bedingungen und sind nicht pauschal \u00fcbertragbar.'
    : 'Interpretation: In the test shown (Metaver\u00ae K), the system with metakaolin replacement exceeded the reference cement in pozzolanic index. The combined system with 26 wt.-% clinker replacement (8 wt.-% Metaver\u00ae K + 18 wt.-% limestone powder) approached the level of the reference cement by 28 days. Across the full test series (> 30 mixes), up to 26 wt.-% of clinker could be replaced without a substantial reduction in strength. The reference cement equals 100% by definition. The values apply to the tested conditions and are not generally transferable.';
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'TechArticle',
    headline: t('zement.title'), description: answer, inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}/zementsubstitution`,
    datePublished: PAGE_DATES.zement.published, dateModified: PAGE_DATES.zement.reviewed,
    author: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
    publisher: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
  };
  const toc = [
    { id: 'system', label: t('zement.s1Title') },
    { id: 'chart', label: t('zement.chartTitle') },
    { id: 'methode', label: t('zement.methodTitle') },
    { id: 'rezeptur', label: t('zement.mixTitle') },
    { id: 'einordnung', label: t('zement.s2Title') },
    { id: 'faq', label: 'FAQ' },
    { id: 'quellen', label: t('common.sources') },
  ];
  return (
    <div>
      <SEO title={t('zement.title')} description={answer} path="/zementsubstitution" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={t('zement.label')} title={t('zement.title')} lead={t('zement.lead')} imageKey="concreteArch1" />
      <section className="bg-white py-10 lg:py-14">
        <div className="container-wide"><Breadcrumbs items={[{ label: t('zement.label'), to: '/zementsubstitution' }]} /></div>
        <div className="container-wide mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-5">
              <ReadingMeta minutes={t('zement.readMinutes')} />
              <TableOfContents items={toc} />
              <AuthorMeta dates={PAGE_DATES.zement} />
            </div>
          </aside>
          <article className="lg:col-span-8 order-1 lg:order-2">
            <AnswerBlock>{answer}</AnswerBlock>
            <div id="system" className="mt-10 scroll-mt-24 max-w-2xl">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('zement.s1Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('zement.s1Body')}</p>
            </div>
            <div id="chart" className="mt-10 scroll-mt-24">
              <Reveal>
                <ScienceChart
                  title={t('zement.chartTitle')}
                  unit={c.unit[lang]}
                  categories={c.categories[lang]}
                  series={c.series.map((s) => ({ label: s.label[lang], values: s.values, color: s.color }))}
                  methodology={c.methodology[lang]}
                  summary={chartSummary}
                  tableLabel={t('common.parameter')}
                />
              </Reveal>
            </div>
            <div id="methode" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('zement.methodTitle')}</h2>
              <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left border-collapse min-w-[480px]">
                  <tbody>
                    {CEMENT_SUB_METHOD.map((r, i) => (
                      <tr key={i} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                        <th scope="row" className="px-4 py-3 text-[12.5px] font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap align-top">{r.p[lang]}</th>
                        <td className="px-4 py-3 text-[13.5px] text-slate-700">{r.v[lang]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div id="rezeptur" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('zement.mixTitle')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{t('zement.mixIntro')}</p>
              <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left border-collapse min-w-[480px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {MIX_COMPARISON.columns[lang].map((c, i) => (
                        <th key={i} className={`px-4 py-3 text-[12px] font-bold uppercase tracking-wide ${i === 0 ? 'text-slate-500' : 'text-slate-900'}`}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MIX_COMPARISON.rows.map((r, i) => (
                      <tr key={i} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                        <td className="px-4 py-3 text-[13.5px] font-semibold text-slate-700">{r.c[lang]}</td>
                        <td className="px-4 py-3 text-[13.5px] text-slate-700">{r.ref}</td>
                        <td className="px-4 py-3 text-[13.5px] text-blue-700 font-bold">{r.mk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-[12px] text-slate-500 leading-relaxed max-w-3xl">{MIX_COMPARISON.note[lang]}</p>
            </div>
            <div id="einordnung" className="mt-12 scroll-mt-24 max-w-2xl">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('zement.s2Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('zement.s2Body')}</p>
            </div>
            <div id="faq" className="mt-14 scroll-mt-24">
              <FAQ items={FAQS.zement[lang]} />
            </div>
            <div id="quellen" className="mt-14 scroll-mt-24">
              <SourcesBlock sources={t('zement.sources')} />
            </div>
            <div className="mt-12">
              <RelatedTopics items={RELATED.zement} />
            </div>
          </article>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Zementsubstitution;
